import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Sparkles } from 'lucide-react';
import { SYSTEM_PROMPT } from '../../data/systemPrompt';
import { ChatMarkdown } from './ChatMarkdown';

const WELCOME_MESSAGE = "Hey! 👋 I'm Nihanth's AI assistant - I have access to his full resume, LinkedIn profile, and live GitHub repos right now. Ask me anything about his experience, projects, or skills. I'll give you the real details.";

// Rate limiting config
const TOTAL_LIMIT = 20;          // max messages per session
const OFF_TOPIC_LIMIT = 5;       // max off-topic messages per session

// Keywords that indicate the message IS about Nihanth / his work
const ON_TOPIC_KEYWORDS = [
  'nihanth', 'naidu', 'kalisetti',
  'experience', 'project', 'skill', 'work', 'job', 'role', 'engineer', 'developer',
  'resume', 'cv', 'linkedin', 'github',
  'axiom', 'nexus', 'researchforge', 'pharmallm', 'rag', 'langchain', 'langgraph',
  'ai', 'ml', 'machine learning', 'python', 'llm', 'agent', 'fine-tun',
  'unitygrid', 'digit insurance', 'futzen', 'edge flex',
  'aws', 'certification', 'education', 'liu', 'university',
  'available', 'hire', 'recruit', 'contact', 'email', 'salary', 'rate',
  'tech', 'stack', 'tool', 'framework', 'database', 'vector', 'pgvector',
  'docker', 'kubernetes', 'fastapi', 'react', 'java', 'sql',
  'impression', 'voice', 'thought leadership', 'publication',
  'he ', 'his ', 'him ', 'you ', 'your '
];

const isOffTopic = (text) => {
  const lower = text.toLowerCase();
  return !ON_TOPIC_KEYWORDS.some((kw) => lower.includes(kw));
};

const getRateLimitData = () => {
  try {
    const raw = localStorage.getItem('chatRateLimit');
    if (!raw) return { total: 0, offTopic: 0, date: new Date().toDateString() };
    const data = JSON.parse(raw);
    // Reset daily
    if (data.date !== new Date().toDateString()) {
      return { total: 0, offTopic: 0, date: new Date().toDateString() };
    }
    return data;
  } catch { return { total: 0, offTopic: 0, date: new Date().toDateString() }; }
};

const saveRateLimitData = (data) => {
  localStorage.setItem('chatRateLimit', JSON.stringify(data));
};

const sanitize = (text) => text.replace(/\u2014/g, ' - ').replace(/\u2013/g, ' - ');

const SUGGESTIONS = [
  'Walk me through AXIOM',
  'What makes him different from other AI engineers?',
  'Is he available for roles right now?',
  "What's his strongest technical skill?",
];

const FloatingChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [hasOpened, setHasOpened] = useState(
    () => localStorage.getItem('chatOpened') === 'true'
  );
  const [rateLimitData, setRateLimitData] = useState(() => getRateLimitData());

  const isHardBlocked = rateLimitData.total >= TOTAL_LIMIT;
  const isOffTopicBlocked = rateLimitData.offTopic >= OFF_TOPIC_LIMIT;
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Listen for external open trigger (from Navbar / HeroSection)
  useEffect(() => {
    const handleOpenChat = () => setIsOpen(true);
    window.addEventListener('openChat', handleOpenChat);
    return () => window.removeEventListener('openChat', handleOpenChat);
  }, []);

  // Auto-scroll only the messages container, never the page
  useEffect(() => {
    if (!isOpen || !messagesContainerRef.current) return;
    const container = messagesContainerRef.current;
    container.scrollTop = container.scrollHeight;
  }, [messages, isTyping, isOpen]);

  const toggleChat = () => {
    setIsOpen((prev) => {
      if (!prev && !hasOpened) {
        localStorage.setItem('chatOpened', 'true');
        setHasOpened(true);
      }
      return !prev;
    });
  };

  const sendMessage = async (text) => {
    if (!text.trim() || isTyping) return;

    // Hard block: total session limit reached
    if (isHardBlocked) {
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: text },
        { role: 'assistant', content: "You've reached the message limit for this session. Refresh the page tomorrow for a fresh session, or reach out to Nihanth directly at **nihanthnaidu.kalisetti@my.liu.edu** or [LinkedIn](https://linkedin.com/in/nihanthnaidu007)." },
      ]);
      setInput('');
      return;
    }

    // Off-topic block
    const offTopic = isOffTopic(text);
    if (offTopic && isOffTopicBlocked) {
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: text },
        { role: 'assistant', content: "I'm Nihanth's portfolio assistant. I can only help with questions about his experience, projects, skills, and availability. Feel free to ask me anything about him!" },
      ]);
      setInput('');
      return;
    }

    // Update rate limit counters
    const newRateData = {
      ...rateLimitData,
      total: rateLimitData.total + 1,
      offTopic: offTopic ? rateLimitData.offTopic + 1 : rateLimitData.offTopic,
    };
    saveRateLimitData(newRateData);
    setRateLimitData(newRateData);

    setShowSuggestions(false);

    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setIsTyping(true);

    try {
      const localKey = process.env.REACT_APP_ANTHROPIC_API_KEY;
      let assistantText;

      if (localKey) {
        // Local dev (npm start): call Anthropic directly with REACT_APP_ANTHROPIC_API_KEY
        const response = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': localKey,
            'anthropic-version': '2023-06-01',
            'anthropic-dangerous-direct-browser-access': 'true',
          },
          body: JSON.stringify({
            model: 'claude-haiku-4-5',
            max_tokens: 1000,
            system: SYSTEM_PROMPT,
            messages: newMessages,
          }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error?.message || `API error ${response.status}`);
        }
        const data = await response.json();
        assistantText = sanitize(data.content[0].text);
      } else {
        // Production (Vercel): use serverless function with full dynamic prompt (resume + GitHub)
        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ messages: newMessages }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.detail || err.error || `Server error ${response.status}`);
        }
        const data = await response.json();
        assistantText = sanitize(data.content[0].text);
      }

      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: assistantText },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            err.message === 'Failed to fetch'
              ? "I'm having trouble connecting. Please reach out to Nihanth directly at nihanthnaidu.kalisetti@my.liu.edu!"
              : `Something went wrong: ${err.message}. Reach out to Nihanth at nihanthnaidu.kalisetti@my.liu.edu!`,
        },
      ]);
    }

    setIsTyping(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <>
      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="chat-window"
            initial={{ opacity: 0, scale: 0.85, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            style={{
              position: 'fixed',
              bottom: '100px',
              right: '28px',
              width: '380px',
              height: '520px',
              background: 'rgba(8, 12, 20, 0.97)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(0, 212, 255, 0.3)',
              borderRadius: '20px',
              boxShadow:
                '0 0 60px rgba(0, 212, 255, 0.15), 0 25px 50px rgba(0,0,0,0.6)',
              zIndex: 9998,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            className="chat-window-mobile"
          >
            {/* Header */}
            <div
              style={{
                height: '64px',
                background: 'rgba(0, 212, 255, 0.05)',
                borderBottom: '1px solid rgba(0, 212, 255, 0.15)',
                display: 'flex',
                alignItems: 'center',
                padding: '0 16px',
                gap: '12px',
                flexShrink: 0,
              }}
            >
              {/* Avatar */}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    boxShadow: '0 0 16px rgba(0,212,255,0.35)',
                  }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL || ''}/favicon.svg`}
                    alt="NK"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                {/* Online dot */}
                <span
                  style={{
                    position: 'absolute',
                    bottom: '1px',
                    right: '1px',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: '#39FF14',
                    border: '2px solid rgba(8,12,20,0.97)',
                    animation: 'pulse-glow 2s ease-in-out infinite',
                  }}
                />
              </div>

              {/* Name */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: 'Syne, sans-serif',
                    fontWeight: '700',
                    fontSize: '14px',
                    color: '#F0F4FF',
                    lineHeight: 1.2,
                  }}
                >
                  Nihanth's AI
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={toggleChat}
                aria-label="Close chat"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#8892A4',
                  flexShrink: 0,
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')
                }
              >
                <X size={16} />
              </button>
            </div>

            {/* Messages Area */}
            <div
              ref={messagesContainerRef}
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                scrollbarWidth: 'thin',
                scrollbarColor: '#00D4FF transparent',
              }}
            >
              {/* Welcome message */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #00D4FF, #0088AA)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '2px',
                  }}
                >
                  <Sparkles size={14} color="#080C14" />
                </div>
                <div
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '12px',
                    padding: '10px 14px',
                    fontSize: '13px',
                    color: '#C0C8D8',
                    lineHeight: '1.5',
                    maxWidth: '85%',
                    fontFamily: 'Inter, sans-serif',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  {WELCOME_MESSAGE}
                </div>
              </div>

              {/* Suggestion pills */}
              {showSuggestions && messages.length === 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    paddingLeft: '36px',
                  }}
                >
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(s)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '20px',
                        background: 'rgba(8,12,20,0.8)',
                        border: '1px solid rgba(0,212,255,0.3)',
                        color: '#00D4FF',
                        fontSize: '12px',
                        fontFamily: 'DM Mono, monospace',
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          'rgba(0,212,255,0.1)')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background =
                          'rgba(8,12,20,0.8)')
                      }
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {/* Conversation messages */}
              {messages.map((msg, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    gap: '8px',
                    alignItems: 'flex-start',
                  }}
                >
                  {msg.role === 'assistant' && (
                    <div
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #00D4FF, #0088AA)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        marginTop: '2px',
                      }}
                    >
                      <Sparkles size={14} color="#080C14" />
                    </div>
                  )}
                  <div
                    style={{
                      maxWidth: '80%',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      fontSize: '13px',
                      lineHeight: '1.5',
                      fontFamily: 'Inter, sans-serif',
                      wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    ...(msg.role === 'user'
                        ? {
                            background: 'rgba(0,212,255,0.15)',
                            border: '1px solid rgba(0,212,255,0.3)',
                            color: '#F0F4FF',
                            borderBottomRightRadius: '4px',
                          }
                        : {
                            background: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            color: '#C0C8D8',
                            borderBottomLeftRadius: '4px',
                          }),
                    }}
                  >
                    {msg.role === 'assistant' ? (
                      <ChatMarkdown>{msg.content}</ChatMarkdown>
                    ) : (
                      msg.content
                    )}
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {isTyping && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #00D4FF, #0088AA)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Sparkles size={14} color="#080C14" />
                  </div>
                  <div
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '12px',
                      borderBottomLeftRadius: '4px',
                      padding: '12px 16px',
                      display: 'flex',
                      gap: '5px',
                      alignItems: 'center',
                    }}
                  >
                    {[0, 150, 300].map((delay) => (
                      <span
                        key={delay}
                        style={{
                          width: '7px',
                          height: '7px',
                          borderRadius: '50%',
                          background: '#00D4FF',
                          display: 'inline-block',
                          animation: 'bounce 1.2s ease-in-out infinite',
                          animationDelay: `${delay}ms`,
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div
              style={{
                borderTop: '1px solid rgba(0,212,255,0.15)',
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
              }}
            >
              {isHardBlocked && (
                <div style={{ padding: '5px 12px', fontSize: '11px', color: '#FF6B6B', fontFamily: 'DM Mono, monospace', textAlign: 'center', background: 'rgba(255,107,107,0.06)' }}>
                  Daily session limit reached · resets tomorrow
                </div>
              )}
              <div style={{ height: '64px', display: 'flex', alignItems: 'center', padding: '0 12px', gap: '10px' }}>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isHardBlocked ? 'Session limit reached...' : "Ask about Nihanth's work..."}
                disabled={isTyping || isHardBlocked}
                style={{
                  flex: 1,
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '10px',
                  padding: '10px 14px',
                  fontSize: '13px',
                  color: '#F0F4FF',
                  fontFamily: 'DM Mono, monospace',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  opacity: isTyping ? 0.5 : 1,
                }}
                onFocus={(e) =>
                  (e.currentTarget.style.borderColor = 'rgba(0,212,255,0.5)')
                }
                onBlur={(e) =>
                  (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)')
                }
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || isTyping || isHardBlocked}
                aria-label="Send message"
                style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: '50%',
                  background: !input.trim() || isTyping || isHardBlocked
                    ? 'rgba(0,212,255,0.3)'
                    : 'linear-gradient(135deg, #00D4FF, #0088AA)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: !input.trim() || isTyping || isHardBlocked ? 'not-allowed' : 'pointer',
                  flexShrink: 0,
                  transition: 'all 0.2s',
                  boxShadow: !input.trim() || isTyping || isHardBlocked
                    ? 'none'
                    : '0 0 12px rgba(0,212,255,0.4)',
                }}
              >
                {isTyping ? (
                  <span
                    style={{
                      width: '14px',
                      height: '14px',
                      border: '2px solid rgba(8,12,20,0.6)',
                      borderTopColor: '#080C14',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                      display: 'inline-block',
                    }}
                  />
                ) : (
                  <Send size={15} color="#080C14" />
                )}
              </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAB */}
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 2, type: 'spring', stiffness: 260, damping: 20 }}
        style={{
          position: 'fixed',
          bottom: '28px',
          right: '28px',
          zIndex: 9999,
        }}
      >
        {/* Tooltip */}
        <AnimatePresence>
          {!isOpen && (
            <motion.div
              initial={{ y: 4, scale: 0.9 }}
              animate={{ y: 0, scale: 1 }}
              exit={{ scale: 0.9 }}
              className="fab-tooltip"
              style={{
                position: 'absolute',
                bottom: '72px',
                right: '0',
                background: 'rgba(8,12,20,0.95)',
                border: '1px solid rgba(0,212,255,0.3)',
                borderRadius: '8px',
                padding: '6px 12px',
                whiteSpace: 'nowrap',
                fontSize: '12px',
                fontFamily: 'DM Mono, monospace',
                color: '#00D4FF',
                pointerEvents: 'none',
                opacity: 0,
                transition: 'opacity 0.2s',
              }}
            >
              Chat with Nihanth's AI
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          onClick={toggleChat}
          aria-label={isOpen ? 'Close chat' : 'Open chat with AI assistant'}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, #00D4FF 0%, #0088AA 100%)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            boxShadow:
              '0 0 20px rgba(0,212,255,0.5), 0 0 40px rgba(0,212,255,0.2)',
            transition: 'box-shadow 0.3s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow =
              '0 0 30px rgba(0,212,255,0.8), 0 0 60px rgba(0,212,255,0.3)';
            const tooltip = e.currentTarget.parentElement.querySelector('.fab-tooltip');
            if (tooltip) tooltip.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow =
              '0 0 20px rgba(0,212,255,0.5), 0 0 40px rgba(0,212,255,0.2)';
            const tooltip = e.currentTarget.parentElement.querySelector('.fab-tooltip');
            if (tooltip) tooltip.style.opacity = '0';
          }}
        >
          {/* Notification dot */}
          {!hasOpened && (
            <span
              style={{
                position: 'absolute',
                top: '2px',
                right: '2px',
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: '#FF4444',
                border: '2px solid #080C14',
                animation: 'pulse-glow 1.5s ease-in-out infinite',
              }}
            />
          )}

          {/* Icon: bot or X */}
          <motion.div
            animate={{ rotate: isOpen ? 90 : 0, scale: isOpen ? 0.85 : 1 }}
            transition={{ duration: 0.25 }}
          >
            {isOpen ? (
              <X size={26} color="#080C14" strokeWidth={2.5} />
            ) : (
              <Sparkles size={28} color="#080C14" strokeWidth={2} />
            )}
          </motion.div>
        </motion.button>
      </motion.div>

      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-6px); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @media (max-width: 480px) {
          .chat-window-mobile {
            width: calc(100vw - 32px) !important;
            right: 16px !important;
            bottom: 90px !important;
          }
        }
        [data-chat-fab] {
          bottom: 20px !important;
          right: 16px !important;
        }
      `}</style>
    </>
  );
};

export default FloatingChatbot;
